import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { updateSearchParams } from "../../../utils/searchParams";
import { Alert } from "../../common/Alert";
import {
  MarkdownArticleLayout,
  SidebarGroup,
  SidebarItem,
} from "./MarkdownArticleLayout";

interface WikiArticle {
  slug: string;
  title: string;
  description: string;
  category?: string;
  date: string;
  children?: WikiArticle[];
}

interface WikiManifest {
  articles: WikiArticle[];
}

interface FlatArticle {
  slug: string;
  title: string;
  date: string;
  category: string;
  indent: boolean;
}

function flattenArticles(articles: WikiArticle[]): FlatArticle[] {
  const result: FlatArticle[] = [];
  for (const article of articles) {
    const category = article.category ?? "";
    result.push({
      category,
      date: article.date,
      indent: false,
      slug: article.slug,
      title: article.title,
    });
    if (article.children) {
      for (const child of article.children) {
        result.push({
          category,
          date: child.date,
          indent: true,
          slug: child.slug,
          title: child.title,
        });
      }
    }
  }
  return result;
}

const ARTICLE_PARAM = "article";

export const WikiView: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [manifest, setManifest] = useState<WikiArticle[]>([]);
  const [manifestError, setManifestError] = useState<string | null>(null);

  const flatArticles = useMemo(() => flattenArticles(manifest), [manifest]);

  const slugFromUrl = searchParams.get(ARTICLE_PARAM);
  const hasMatch = flatArticles.some((a) => a.slug === slugFromUrl);
  const selectedSlug = hasMatch ? slugFromUrl : (flatArticles[0]?.slug ?? null);

  const selectArticle = useCallback(
    (slug: string) => {
      setSearchParams(
        (prev) => updateSearchParams(prev, { [ARTICLE_PARAM]: slug }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    fetch("/wiki/articles.json")
      .then((res) => res.json())
      .then((data: WikiManifest) => {
        setManifest(data.articles);
      })
      .catch(() => setManifestError("Failed to load wiki articles"));
  }, []);

  const selectedArticle = flatArticles.find((a) => a.slug === selectedSlug);

  const groupedByCategory = useMemo(() => {
    const groups: Record<string, FlatArticle[]> = {};
    for (const article of flatArticles) {
      const list = groups[article.category] ?? [];
      list.push(article);
      groups[article.category] = list;
    }
    return groups;
  }, [flatArticles]);

  if (manifestError) {
    return <Alert variant="error">{manifestError}</Alert>;
  }

  return (
    <MarkdownArticleLayout
      sidebarTitle="Articles"
      sidebar={Object.entries(groupedByCategory).map(
        ([category, categoryArticles]) => (
          <SidebarGroup key={category} label={category}>
            {categoryArticles.map((article) => (
              <SidebarItem
                key={article.slug}
                selected={article.slug === selectedSlug}
                onClick={() => selectArticle(article.slug)}
                label={article.title}
                indent={article.indent}
              />
            ))}
          </SidebarGroup>
        ),
      )}
      contentTitle={selectedArticle?.title ?? "Select an article"}
      contentSubtitle={selectedArticle?.date}
      loadingLabel="Loading article..."
      markdownUrl={selectedSlug ? `/wiki/${selectedSlug}.md` : null}
      errorPrefix={`Failed to load article "${selectedSlug}"`}
    />
  );
};
